// src/AppRoutes.tsx
import React, {
    useContext,
    useEffect,
    useMemo,
} from "react";
import {
    Routes,
    Route,
    useLocation,
    useNavigate,
} from "react-router-dom";

import { AuthContext } from "./Auth/AuthProvider";
import { configService } from "./Services/Config/ConfigService";

// Auth / gates
import PreloginFirstGate from "./Auth/PreloginFirstGate";
import { AuthorizeRoute } from "./Auth/AuthorizeRoute";

// Layout & pages
import Layout from "./Layout/Components/Layout";
import Dashboard from "./Dashboard/Dashboard";
import { ActiveRoster } from "./Dashboard/ActiveRoster";
import { AlumniRoster } from "./Dashboard/AlumniRoster";
import { TransferResidents } from "./Dashboard/TransferResidents";
import { Reports } from "./Dashboard/Reports";
import { Requirements } from "./Dashboard/ProgramRequirements";
import { ActiveResident } from "./Dashboard/ActiveResident";
import { AlumniResident } from "./Dashboard/AlumniDetail";

// Misc pages
import PreloginPage from "./PreloginPage";
import PasswordPage from "./PasswordPage";
import ForgotPassword from "./Auth/ForgotPassword";
import { Logout } from "./Auth/Logout";
import ErrorPage from "./ErrorPage";
import NotFoundPage from "./NotFoundPage";
import UnauthorizedPage from "./UnauthorizedPage";

// Utilities
import { PreCertPaths } from "./Shared/Utility/PreCertLocationHelper";

// Spinner
import AbpnSpinner from "./Shared/Components/AbpnSpinner";

// Contexts
import { ActiveRosterProvider } from "./Contexts/ActiveRosterProvider";
import { AlumniRosterProvider } from "./Contexts/AlumniRosterProvider";
import { ResidentInformationForm } from "./Residents/Components/ResidentInformationForm";

//
// 🔹 Special handler for /onbehalfof/:preCertUserId?oboToken=...
//
const OnBehalfOfHandler: React.FC = () => {
    const auth = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const run = async () => {
            const { pathname, search } = location;

            console.log("[OnBehalfOfHandler] start", {
                pathname,
                search,
            });

            try {
                // Make sure MSAL/AuthService is initialized
                await auth.onReady?.();

                // /onbehalfof/4175
                const match = /^\/onbehalfof\/(\d+)/i.exec(pathname || "");
                const preCertUserIdStr = match?.[1] || "";

                const params = new URLSearchParams(search || "");
                const oboToken =
                    params.get("oboToken") ||
                    params.get("obotoken") ||
                    "";

                console.log("[OnBehalfOfHandler] parsed inputs", {
                    preCertUserIdStr,
                    oboTokenPresent: !!oboToken,
                });

                if (!preCertUserIdStr || !oboToken) {
                    console.error(
                        "[OnBehalfOfHandler] Missing preCertUserId or oboToken"
                    );
                    navigate(PreCertPaths.ErrorPage, { replace: true });
                    return;
                }

                const preCertUserId = parseInt(preCertUserIdStr, 10);
                if (!Number.isFinite(preCertUserId) || preCertUserId <= 0) {
                    console.error(
                        "[OnBehalfOfHandler] Invalid preCertUserId",
                        preCertUserIdStr
                    );
                    navigate(PreCertPaths.ErrorPage, { replace: true });
                    return;
                }

                // Call your PreCert API to validate the OBO token
                const cfg = await configService.getConfiguration();
                const baseApi = (cfg.PreCertApiUrl ?? "").replace(/\/+$/, "");
                const url = `${baseApi}/api/PreCertOnBehalfOf/validate`;

                console.log("[OnBehalfOfHandler] calling validate OBO", {
                    url,
                    preCertUserId,
                });

                const resp = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify({
                        preCertUserId,
                        oboToken,
                    }),
                });

                if (!resp.ok) {
                    const text = await resp.text().catch(() => "");
                    console.error(
                        "[OnBehalfOfHandler] validate OBO failed",
                        {
                            status: resp.status,
                            text,
                        }
                    );
                    navigate(PreCertPaths.ErrorPage, { replace: true });
                    return;
                }

                const data = await resp.json().catch((): null => null);
                console.log(
                    "[OnBehalfOfHandler] validate OBO success",
                    data
                );

                // Store OBO state for AbpnClient / middleware
                auth.setOnBehalfOf(String(preCertUserId));
                auth.setOboToken(oboToken);

                console.log(
                    "[OnBehalfOfHandler] OBO state set; navigating to '/' (dashboard root)"
                );

                // With basename="/precert", "/" -> "/precert/"
                navigate("/", { replace: true });
            } catch (e) {
                console.error("[OnBehalfOfHandler] error", e);
                navigate(PreCertPaths.ErrorPage, { replace: true });
            }
        };

        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    // While doing OBO handshake, just show spinner
    return <AbpnSpinner />;
};

const AppRoutes: React.FC = () => {
    const location = useLocation();

    const path = location.pathname || "";
    const isOboRoute = useMemo(
        () => /^\/onbehalfof\/\d+/i.test(path),
        [path]
    );

    useEffect(() => {
        console.log("[AppRoutes] location", {
            path: location.pathname,
            search: location.search,
            hash: location.hash,
            isOboRoute,
        });
    }, [location.pathname, location.search, location.hash, isOboRoute]);

    // 🔑 Short-circuit for OBO URLs:
    if (isOboRoute) {
        console.log("[AppRoutes] short-circuit to OnBehalfOfHandler");
        return <OnBehalfOfHandler />;
    }

    // Normal app routing, with the prelogin gate
    return (
        <>
            <PreloginFirstGate />

            <Routes>
                {/* Everything under the standard Layout */}
                <Route element={<Layout />}>
                    {/* Public-ish routes */}
                    <Route path="password" element={<PasswordPage />} />
                    <Route path="prelogin" element={<PreloginPage />} />
                    <Route
                        path="authentication/logout"
                        element={<Logout />}
                    />
                    <Route path="forgot" element={<ForgotPassword />} />

                    {/* Protected area: requires auth + profile */}
                    <Route
                        element={
                            <AuthorizeRoute>
                                    <Dashboard />
                            </AuthorizeRoute>
                        }
                    >
                        <Route
                            path="Alumni/:physicianId"
                            element={
                                <AlumniRosterProvider>
                                    <AlumniResident />
                                </AlumniRosterProvider>
                            }
                        />
                        <Route
                            path="Alumni/*"
                            element={
                                <AlumniRosterProvider>
                                    <AlumniRoster />
                                </AlumniRosterProvider>
                            }
                        />

                        <Route
                            path="Transfer"
                            element={<TransferResidents />}
                        />
                        <Route path="Reports" element={<Reports />} />
                        <Route
                            path="Requirements"
                            element={<Requirements />}
                        />

                        <Route
                            path="Edit/:physicianId"
                            element={<ResidentInformationForm />}
                        />
                        <Route
                            path="New"
                            element={<ResidentInformationForm />}
                        />

                        {/* Default inside protected area = ActiveRoster */}
                        <Route
                            index
                            element={
                                <ActiveRosterProvider>
                                    <ActiveRoster />
                                </ActiveRosterProvider>
                            }
                        />

                        <Route
                            path=":physicianId"
                            element={
                                <ActiveRosterProvider>
                                    <ActiveResident />
                                </ActiveRosterProvider>
                            }
                        />
                    </Route>

                    {/* System pages */}
                    <Route
                        path={PreCertPaths.ErrorPage}
                        element={<ErrorPage />}
                    />
                    <Route
                        path={PreCertPaths.UnauthorizedPage}
                        element={<UnauthorizedPage />}
                    />

                    {/* Final catch-all */}
                    <Route path="*" element={<NotFoundPage />} />
                </Route>
            </Routes>
        </>
    );
};

export default AppRoutes;
